import type { AnchorRow, Answer, FindingTeaser, RunView, VisualCheck } from '../shared/types.js';
import { ApiError, shotUrl } from './api.js';
import { boot, formatDate, h, quoted, t, tobj } from './dom.js';
import { pageLong, quoteNode, renderFinding, renderPagesList, renderStream, findObservation, tagClass } from './render-shared.js';

export interface BefundActions {
  sendEmail(email: string, marketing: boolean): Promise<{ ok: boolean; delivery: string; link?: string }>;
  cta(): void;
  remove(): Promise<void>;
}

const STATUS_CLASS: Record<string, string> = {
  sichtbar: 'ok', 'nur als Wort': 'part', 'nicht sichtbar': 'bad', 'nicht prüfbar': 'na',
  stimmt: 'ok', weicht_ab: 'part', 'weicht ab': 'part', nicht_pruefbar: 'na', fehlt: 'na', genannt: 'ok',
  belegt: 'ok', teilweise: 'part', 'nicht erwartet': 'na'
};

function answersCount(n: number): string {
  return tobj<Record<string, string>>('befund.answers_count')[String(n)] ?? `${n} Antworten`;
}

function findingsCount(n: number): string {
  return tobj<Record<string, string>>('befund.findings_count')[String(n)] ?? String(n);
}

/** An answer in «Deine Angaben»: what was answered, and under it the question it answers. */
function answerText(view: RunView, answer: Answer): { main: string; note: string } | null {
  const question = view.questions?.find((q) => q.id === answer.id);
  if (answer.skipped) return { main: question?.question ?? answer.id, note: t('befund.answer_skipped') };
  if (answer.id === 'R0') return null;
  const note = question?.question ?? '';
  const textOf = (value: string) => question?.options?.find((option) => option.value === value)?.text ?? value;
  if (question?.type === 'multiple') return { main: (Array.isArray(answer.value) ? answer.value : []).map(textOf).join(', '), note };
  if (question?.type === 'text') return { main: typeof answer.value === 'string' ? answer.value : '', note };
  const base = answer.value === 'other' && answer.other ? answer.other : typeof answer.value === 'string' ? textOf(answer.value) : '';
  return { main: answer.followup ? `${base}: ${quoted(answer.followup)}` : base, note };
}

export function renderBefund(view: RunView, actions: BefundActions): HTMLElement {
  const root = h('section', { class: 'stage on' });
  const b = view.befund!;
  const mit = view.variant === 'mit';
  const pagesRead = view.pages.filter((page) => !page.missing && page.role !== 'impressum').length;

  if (view.full) root.append(h('span', { class: 'badge' }, t('befund.full.badge')));

  // The earlier stages, collapsed; they sit in the footer. The stream is part of the full Befund only.
  const pastLesen = h('details', { class: 'past' }, h('summary', {}, h('span', {}, h('b', {}, t('befund.past_lesen_title')), `: ${pagesRead === 3 ? t('befund.past_lesen_summary') : t('befund.past_lesen_summary_short', { n: pagesRead })}`)), h('div', { class: 'body' }, renderPagesList(view.list, view.pages), view.stream.length ? renderStream(view.stream, true) : null));
  const answerItems: HTMLElement[] = [];
  if (mit && view.description) {
    const confirmed = (view.absicht_confirmed ?? []).map((element) => element.text).join('; ');
    answerItems.push(h('li', {}, t('befund.answer_description'), h('span', {}, confirmed ? t('befund.answer_confirmed', { items: confirmed }) : quoted(view.description))));
  }
  const labels = tobj<Record<string, string>>('befund.answers_labels');
  const answeredIds: string[] = [];
  for (const answer of view.answers ?? []) {
    const text = answerText(view, answer);
    if (!text) continue;
    if (!answer.skipped) answeredIds.push(answer.id);
    answerItems.push(h('li', {}, text.main, h('span', {}, text.note)));
  }
  // Named by the field each question asked about.
  const rest = (view.answers ?? []).filter((a) => a.id !== 'R0').map((a) => labels[view.questions?.find((q) => q.id === a.id)?.anchor ?? ''] ?? a.id).join(', ');
  const pastAnswers = h('details', { class: 'past' }, h('summary', {}, h('span', {}, h('b', {}, t('befund.past_answers_title')), `: ${mit ? t('befund.answers_summary_mit', { rest }) : t('befund.answers_summary_ohne', { rest })}`)), h('div', { class: 'body' }, h('ul', { class: 'answers' }, answerItems)));

  root.append(h('h1', {}, `${t('befund.h1')} `, h('b', {}, t('befund.h1_for', { domain: view.domain }))));
  const answered = answeredIds.filter((id) => id !== 'R0').length;
  const total = b.findings_total;
  root.append(
    h('p', { class: 'lead' }, `${mit ? t('befund.lead_mit', { observations: b.observations_count, answers: answersCount(answered) }) : t('befund.lead_ohne', { observations: b.observations_count, answers: answersCount(answered) })} ${total === 1 ? t('befund.lead_findings_one') : t('befund.lead_findings', { n: findingsCount(total) })}`)
  );
  if (view.cached) root.append(h('p', { class: 'notice' }, t('befund.cached_line', { date: formatDate(view.created_at) })));
  // What was read and what was not: the limit of every sentence that follows, in both views.
  if (b.pruefgrenze) root.append(h('p', { class: 'notice', id: 'pruefgrenze' }, b.pruefgrenze));

  // Intention against the pages
  if (mit && b.absicht_block && view.description) {
    const statuses = tobj<Record<string, string>>('befund.absicht_status');
    const block = h('div', { class: 'absicht' }, h('h2', {}, t('befund.absicht_h2')), h('div', { class: 'q own' }, quoted(view.description), h('span', { class: 'src' }, t('befund.absicht_src'))));
    block.append(
      h('ul', { class: 'checks' }, b.absicht_block.map((row) => h('li', {}, h('span', {}, h('b', {}, `${row.element}:`), ` ${row.text}`, row.page && row.status !== 'nicht sichtbar' ? h('span', { class: 'small muted' }, ` (${t('befund.absicht_page_on', { page: pageLong(row.page) })})`) : null), h('span', { class: `st ${STATUS_CLASS[row.status] ?? 'na'}` }, statuses[row.status] ?? row.status))))
    );
    if (b.absicht_fazit) block.append(h('p', { class: 'cons' }, h('b', {}, t('befund.consequence_label')), ' ', b.absicht_fazit));
    root.append(block);
  }

  if (b.lead) root.append(renderFinding(view, b.lead, { lead: true, quoteClass: b.lead.class === 'Sichtbar und belegt' ? 'ok' : '' }));

  // The free view is a preview: the first finding and the Erscheinungsbild in full, everything else named with a
  // lock. Every lock leads to the one email card further down.
  const ask = view.full ? null : renderAsk(view, actions);
  const request = () => ask?.open();

  if (b.more.length || b.locked.length || b.checks.length) root.append(h('h2', {}, t('befund.more_h2')));
  for (const finding of b.more) root.append(renderFinding(view, finding, { quoteClass: finding.class === 'Als Wort sichtbar, nicht als Ausdruck' ? 'part' : '' }));
  root.append(renderVisual(view));
  if (b.locked.length) {
    root.append(renderLocked(b.locked, request));
    root.append(lockedLine(countText('befund.locked_more', b.locked.length), request));
  }
  if (view.full && b.withheld?.length) {
    root.append(h('h2', {}, t('befund.withheld_h2')));
    for (const finding of b.withheld) root.append(renderFinding(view, finding));
  }

  // Who is behind it
  root.append(h('h2', {}, t('befund.who_h2')));
  const who = tobj<Record<string, string>>('befund.who_status');
  const pill = (status: string) => h('span', { class: `st ${STATUS_CLASS[status] ?? 'na'}` }, who[status] ?? status);
  root.append(h('ul', { class: 'who' }, b.entity_lines.map((line) => h('li', {}, h('span', {}, line.text), pill(line.status)))));
  if (b.entity_locked.length) root.append(lockedLine(countText('befund.locked_who', b.entity_locked.length), request, b.entity_locked.map(pill)));
  root.append(h('p', { class: 'small muted', style: 'margin-top:8px' }, t('befund.who_note')));

  if (b.works) {
    const card = renderFinding(view, b.works, { quoteClass: 'ok' });
    card.style.marginTop = '28px';
    root.append(card);
  }

  // The preview leaves the limits to the footer line.
  if (view.full) {
    root.append(h('h2', {}, t('befund.not_measured_h2')));
    root.append(h('p', {}, b.nicht_gemessen, b.missing_roles.length ? ` ${t('befund.not_measured_missing', { pages: b.missing_roles.map(pageLong).join(', ') })}` : ''));
    root.append(renderFullExtras(view));
  }

  if (ask) root.append(ask.el);

  // CTA: the heading is the promise of the route (what the 30 minutes deliver), the reason is the model's sentence
  // in the visitor's terms, the button carries the route's label. «gut» and «selbst-korrigieren» offer no call.
  const cta = h('div', { class: 'cta', id: 'cta' });
  if (!b.next_step.url) {
    cta.append(h('h3', {}, b.next_step.label ?? t('befund.cta_self')), h('p', {}, b.next_step.reason));
    if (b.next_step.promise) cta.append(h('p', { class: 'small muted', style: 'margin:12px 0 0' }, b.next_step.promise));
  } else {
    const button = h('a', { class: 'btn', href: b.next_step.url, target: '_blank', rel: 'noopener' }, b.next_step.label || t('befund.cta_button'));
    button.addEventListener('click', () => actions.cta());
    cta.append(h('h3', {}, b.next_step.promise || b.next_step.label || ''), h('p', {}, b.next_step.reason), button, h('p', { class: 'small muted', style: 'margin:12px 0 0' }, t('befund.cta_note')));
  }
  root.append(cta);

  // Footer with delete
  const del = h('a', { href: '#' }, t('befund.delete'));
  const confirm = h('span', { hidden: true }, ` ${t('befund.delete_confirm')} `, h('button', { class: 'link', type: 'button' }, t('befund.delete_yes')), ' ', h('button', { class: 'link', type: 'button' }, t('befund.delete_no')));
  del.addEventListener('click', (event) => {
    event.preventDefault();
    confirm.hidden = false;
    del.hidden = true;
  });
  const [yes, no] = Array.from(confirm.querySelectorAll('button'));
  no.addEventListener('click', () => {
    confirm.hidden = true;
    del.hidden = false;
  });
  yes.addEventListener('click', () => void actions.remove());
  root.append(h('footer', { class: 'note' }, pastLesen, answerItems.length ? pastAnswers : null, h('p', {}, t('befund.footer', { pages_count: findingsCount(pagesRead).toLowerCase(), domain: view.domain, date: formatDate(view.created_at) }), ' ', del, confirm)));
  return root;
}

const LOCK = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><path fill="currentColor" d="M8 1a3.5 3.5 0 0 0-3.5 3.5V7H4a1.5 1.5 0 0 0-1.5 1.5v5A1.5 1.5 0 0 0 4 15h8a1.5 1.5 0 0 0 1.5-1.5v-5A1.5 1.5 0 0 0 12 7h-.5V4.5A3.5 3.5 0 0 0 8 1Zm2 6H6V4.5a2 2 0 1 1 4 0V7Z"/></svg>';

/** «Ein …» for one, «{n} …» with the number as a word from two. */
function countText(key: string, n: number): string {
  return n === 1 ? t(`${key}.1`) : t(`${key}.n`, { n: findingsCount(n) });
}

/** The findings the preview holds back: class and priority, how many quotes, which pages. Never the finding itself. */
function renderLocked(locked: FindingTeaser[], request: () => void): HTMLElement {
  const classes = tobj<Record<string, string>>('befund.classes');
  return h(
    'ul',
    { class: 'locked' },
    locked.map((item) => {
      const pages = item.pages.length >= 3 ? t('befund.all_pages') : item.pages.map(pageLong).join(', ');
      const meta = [item.quotes ? countText('befund.locked_quotes', item.quotes) : '', pages].filter(Boolean).join(' · ');
      const label = classes[item.class] ?? item.class;
      const row = h('button', { class: 'lockrow', type: 'button', 'aria-label': `${label}. ${t('befund.locked_request')}` }, h('span', { class: `tag ${tagClass(item)}` }, label), meta ? h('span', { class: 'meta' }, meta) : null, h('span', { class: 'lock', html: LOCK }));
      row.addEventListener('click', request);
      return h('li', {}, row);
    })
  );
}

/** The line under a shortened section: what the full Befund adds there, as the way to it. */
function lockedLine(text: string, request: () => void, pills: HTMLElement[] = []): HTMLElement {
  const button = h('button', { class: 'link', type: 'button' }, text);
  button.addEventListener('click', request);
  return h('p', { class: 'more-line' }, h('span', { class: 'lock', html: LOCK }), button, pills.length ? h('span', { class: 'pills' }, pills) : null);
}

/** The email card of the preview: what the full Befund holds, one field, one button. The link goes out by mail only,
 * so the address is a working one. */
function renderAsk(view: RunView, actions: BefundActions): { el: HTMLElement; open(): void } {
  const b = view.befund!;
  // What the full Befund adds, as outcomes: the other findings with their next steps, in order; never a count of
  // numbers. The Kennwerte join the list only while they are switched on.
  const items = [
    b.locked.length ? countText('befund.email_items.findings', b.locked.length) : '',
    b.locked.length > 1 ? t('befund.email_items.order') : '',
    b.entity_locked.length ? countText('befund.email_items.who', b.entity_locked.length) : '',
    t('befund.email_items.observations', { observations: b.observations_count }),
    boot.config.kennwerte ? t('befund.email_items.kennwerte') : ''
  ].filter(Boolean);
  const email = h('input', { type: 'email', inputmode: 'email', placeholder: t('befund.email_placeholder'), 'aria-label': t('befund.email_label'), autocomplete: 'email' }) as HTMLInputElement;
  const marketing = h('input', { type: 'checkbox' }) as HTMLInputElement;
  const send = h('button', { class: 'btn block', type: 'submit' }, t('befund.email_submit'));
  const errorBox = h('p', { class: 'error', hidden: true });
  const fields = h('div', { hidden: view.email_sent }, errorBox, h('label', { class: 'field' }, h('span', {}, t('befund.email_label')), email), h('label', { class: 'check' }, marketing, ' ', h('span', {}, t('befund.email_marketing'))), send, h('p', { class: 'small muted', style: 'margin:10px 0 0' }, t('befund.email_note')));
  // After a send: the confirmation, and a way back for a mistyped address.
  const sentText = h('span', {}, t('befund.email_sent'));
  const change = h('button', { class: 'link', type: 'button' }, t('befund.email_change'));
  const sent = h('p', { class: 'sentmsg', hidden: !view.email_sent }, sentText, ' ', change);
  change.addEventListener('click', () => {
    sent.hidden = true;
    fields.hidden = false;
    send.disabled = false;
    email.focus();
  });
  const form = h('form', { class: 'card', novalidate: 'novalidate' }, h('ul', { class: 'gets' }, items.map((item) => h('li', {}, item))), fields, sent);
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    errorBox.hidden = true;
    const address = email.value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(address)) {
      errorBox.textContent = t('befund.email_invalid');
      errorBox.hidden = false;
      return;
    }
    send.disabled = true;
    try {
      const result = await actions.sendEmail(address, marketing.checked);
      if (!result.ok) throw new Error('failed');
      sentText.textContent = result.delivery === 'logged' ? t('befund.email_logged') : t('befund.email_sent_to', { email: address });
      sentText.querySelector('a')?.remove();
      if (result.link && result.delivery === 'logged') sentText.append(' ', h('a', { href: result.link }, t('befund.full.badge')));
      fields.hidden = true;
      sent.hidden = false;
    } catch (error) {
      errorBox.textContent = error instanceof ApiError && error.code === 'email_limit' ? t('befund.email_limit') : t('befund.email_failed');
      errorBox.hidden = false;
      send.disabled = false;
    }
  });
  const el = h('div', { id: 'full' }, h('h2', {}, t('befund.email_h2')), form);
  return {
    el,
    open() {
      const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: calm ? 'auto' : 'smooth', block: 'start' });
      if (!fields.hidden) email.focus({ preventScroll: true });
    }
  };
}

function renderVisual(view: RunView): HTMLElement {
  const b = view.befund!;
  const card = h('div', { class: 'finding' });
  const checks = b.checks;
  if (!checks.length) {
    card.hidden = true;
    return card;
  }
  if (b.erscheinungsbild) {
    card.append(h('span', { class: 'tag mid' }, tobj<Record<string, string>>('befund.classes')['Uneinheitliches Erscheinungsbild']), h('h3', {}, b.erscheinungsbild.title));
  } else {
    // The heading counts the pages that were compared: three, two, or not all checks possible.
    const allOk = checks.every((check) => check.status === 'stimmt');
    card.append(h('h3', {}, b.pages_read >= 3 && allOk ? t('befund.visual_h3_ok') : b.pages_read === 2 && allOk ? t('befund.visual_h3_ok_two') : t('befund.visual_h3_na')));
  }
  const cropLabels = tobj<Record<string, string>>('befund.visual_crop_labels');
  const colourCheck = checks.find((check) => check.id === 'colours_same');
  const logoCheck = checks.find((check) => check.id === 'logo_same');
  // One miniature per page (the fold, as the preview's sketches) with the crops the checks compare underneath:
  // the logo, and the primary button when the colour check has something to show. Header crops are 1280 px
  // wide strips and only legible at full width; they sit in the full view.
  const mocks = h('div', { class: 'mocks' });
  let any = false;
  for (const role of ['start', 'ueber-uns', 'leistungen'] as const) {
    const page = view.pages.find((p) => p.role === role);
    const shots = page && !page.missing ? page.screenshots : null;
    const label = cropLabels[role] ?? role;
    const thumb = shotUrl(view.id, shots?.thumb ?? shots?.fold ?? null);
    const fullPage = shotUrl(view.id, shots?.full ?? null) ?? thumb;
    const header = shotUrl(view.id, shots?.header ?? null);
    const logo = logoCheck && logoCheck.status !== 'nicht_pruefbar' ? shotUrl(view.id, shots?.logo ?? null) : null;
    const cta = colourCheck && colourCheck.status !== 'nicht_pruefbar' ? shotUrl(view.id, shots?.cta ?? null) : null;
    const colour = b.colours?.[role] ?? null;
    if (thumb || logo || cta) any = true;
    const evidence = h(
      'div',
      { class: 'ev' },
      logo ? zoomable(logo, `${label}: Logo`, header ?? logo, t('befund.visual_logo_caption', { page: pageLong(role) })) : null,
      cta ? zoomable(cta, `${label}: Knopf`, cta, t('befund.visual_cta_caption', { page: pageLong(role) })) : colour && !thumb ? h('span', { class: 'sw', style: `background:${colour}` }) : null
    );
    mocks.append(
      h(
        'div',
        { class: 'mock' },
        thumb ? zoomable(thumb, label, fullPage ?? thumb, t('befund.visual_full_page', { page: pageLong(role) }), 'thumb') : colour ? h('span', { class: 'sw', style: `background:${colour}` }) : null,
        evidence,
        label
      )
    );
  }
  if (any) mocks.append(h('p', { class: 'small muted hint' }, t('befund.visual_zoom')));
  if (any || b.colours) card.append(mocks);
  const labels = tobj<Record<string, string>>('befund.visual_checks');
  const statuses = tobj<Record<string, string>>('befund.visual_status');
  const order: VisualCheck['id'][] = ['logo_same', 'fonts_same', 'headline_dominant', 'colours_same'];
  const sorted = [...checks].sort((a, c) => order.indexOf(a.id) - order.indexOf(c.id));
  // The fixed labels count three pages; with fewer, the model's sentence names the pages that were compared.
  card.append(h('ul', { class: 'checks' }, sorted.map((check) => h('li', {}, h('span', {}, check.status === 'stimmt' && b.pages_read >= 3 ? labels[check.id] ?? check.sentence : check.sentence || labels[check.id]), h('span', { class: `st ${STATUS_CLASS[check.status] ?? 'na'}` }, statuses[check.status] ?? check.status)))));
  card.append(h('p', { style: 'margin-top:12px' }, t('befund.visual_note')));
  if (b.erscheinungsbild) card.append(h('p', { class: 'cons' }, h('b', {}, t('befund.consequence_label')), ' ', b.erscheinungsbild.konsequenz));
  return card;
}

/** An image that opens larger in a lightbox: the whole page for a miniature, the header for a logo, the button itself. */
function zoomable(src: string, alt: string, target: string, caption: string, imgClass = ''): HTMLElement {
  const button = h('button', { class: 'zoom', type: 'button', 'aria-label': `${alt}. ${t('befund.visual_zoom')}` }, h('img', { class: imgClass, src, alt, loading: 'lazy' }));
  button.addEventListener('click', () => openLightbox(target, caption));
  return button;
}

function openLightbox(src: string, caption: string): void {
  const onKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') close();
  };
  const close = () => {
    overlay.remove();
    document.removeEventListener('keydown', onKey);
  };
  const img = h('img', { src, alt: caption }) as HTMLImageElement;
  img.addEventListener('load', () => {
    // Element crops are small; show them at least twice their size so a button or a logo is readable.
    if (img.naturalWidth < 480) img.style.width = `${Math.min(img.naturalWidth * 2, window.innerWidth * 0.9)}px`;
  });
  const closeButton = h('button', { class: 'link', type: 'button' }, t('befund.visual_close'));
  closeButton.addEventListener('click', close);
  const overlay = h('div', { class: 'lightbox', role: 'dialog', 'aria-modal': 'true', 'aria-label': caption }, h('figure', {}, img, h('figcaption', {}, h('span', {}, caption), closeButton)));
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) close();
  });
  document.addEventListener('keydown', onKey);
  document.body.append(overlay);
  closeButton.focus();
}

function renderFullExtras(view: RunView): HTMLElement {
  const b = view.befund!;
  const wrap = h('div');
  // All observations
  wrap.append(h('h2', {}, t('befund.full.observations_h2')));
  wrap.append(h('div', {}, view.observations.map((observation) => quoteNode(observation, observation.absent ? '' : 'ok'))));
  // Matrix
  if (b.matrix?.length) {
    wrap.append(h('h2', {}, t('befund.full.matrix_h2')));
    wrap.append(h('div', { class: 'matrix-wrap' }, renderMatrix(view, b.matrix)));
  }
  // Visual details: header crops at full width (legible), then colours and fonts per page
  if (b.colours || b.fonts || view.pages.some((page) => page.screenshots?.header)) {
    wrap.append(h('h2', {}, t('befund.full.visual_h2')));
    const headers = h('div', { class: 'headers' });
    for (const role of ['start', 'ueber-uns', 'leistungen'] as const) {
      const page = view.pages.find((p) => p.role === role);
      const url = page && !page.missing ? shotUrl(view.id, page.screenshots?.header ?? null) : null;
      if (url) headers.append(h('figure', {}, zoomable(url, pageLong(role), url, `${pageLong(role)}: Kopfbereich`), h('figcaption', {}, pageLong(role))));
    }
    if (headers.childElementCount) wrap.append(headers);
    const dl = h('dl', { class: 'detail' });
    for (const role of ['start', 'ueber-uns', 'leistungen'] as const) {
      const colour = b.colours?.[role];
      const fonts = b.fonts?.[role] ?? [];
      dl.append(h('dt', {}, pageLong(role)), h('dd', {}, colour ? h('span', { class: 'sw', style: `display:inline-block;width:18px;height:18px;vertical-align:middle;margin-right:6px;background:${colour}` }) : null, colour ? `${t('befund.full.visual_colours')} ${colour}. ` : '', fonts.length ? `${t('befund.full.visual_fonts')}: ${fonts.join(', ')}` : ''));
    }
    wrap.append(dl);
  }
  // Kennwerte: only while config.json switches them on (off since 2026-10-12, until their model fits the short flow).
  if (!boot.config.kennwerte) return wrap;
  wrap.append(h('h2', {}, t('befund.full.kennwerte_h2')));
  const k = b.kennwerte;
  if (!k || k.auditbasis_band === 'unzureichend') {
    wrap.append(h('p', {}, t('befund.full.auditbasis_low')));
  } else {
    const names = tobj<Record<string, string>>('befund.full.kennwerte');
    wrap.append(h('div', { class: 'kw' }, (['klarheit', 'sichtbarkeit', 'kohaerenz'] as const).map((key) => h('div', {}, h('b', {}, k[key] === null ? t('befund.full.kennwert_na') : String(k[key])), h('span', {}, names[key])))));
  }
  if (k) wrap.append(h('p', { class: 'small muted' }, `${t('befund.full.auditbasis')}: ${k.auditbasis} (${tobj<Record<string, string>>('befund.full.auditbasis_bands')[k.auditbasis_band]}). ${t('befund.full.kennwerte_note')}`));
  return wrap;
}

function renderMatrix(view: RunView, rows: AnchorRow[]): HTMLElement {
  const anchors = tobj<Record<string, string>>('befund.full.matrix_anchors');
  const statuses = tobj<Record<string, string>>('befund.full.matrix_status');
  const roles = ['start', 'ueber-uns', 'leistungen'] as const;
  return h(
    'table',
    { class: 'matrix' },
    h('thead', {}, h('tr', {}, h('th', {}, t('befund.full.matrix_anchor')), roles.map((role) => h('th', {}, pageLong(role))))),
    h(
      'tbody',
      {},
      rows.map((row) =>
        h(
          'tr',
          {},
          h('td', {}, h('b', {}, anchors[row.anchor] ?? row.anchor), row.canon ? h('div', { class: 'small muted' }, row.canon) : null),
          roles.map((role) => {
            const cell = row.cells[role];
            const first = cell.observation_ids.map((id) => findObservation(view, id)).find((o) => o && !o.absent);
            return h('td', {}, h('span', { class: `st ${STATUS_CLASS[cell.status] ?? 'na'}` }, statuses[cell.status] ?? cell.status), first ? h('div', { class: 'small muted' }, quoted(first.quote ?? '')) : cell.note ? h('div', { class: 'small muted' }, cell.note) : null);
          })
        )
      )
    )
  );
}
